/**
 * WeeklyTrendChart
 * Gráfico de linha mostrando tendência das últimas 4 semanas
 *
 * Fase 1.3 do Plano de Evolução - Mini-histórico
 */

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';
import {
  ChartTooltipBox,
  PainelCard,
  PainelEmpty,
  PainelStat,
  PainelStatRow,
  chartAxisProps,
  chartGridStroke
} from './PainelCard';

export interface WeeklyTrendData {
  weeks: Array<{
    weekLabel: string;
    weekStart: string;
    weekEnd: string;
    received: number;
    processed: number;
    discarded: number;
    pending: number;
  }>;
  totalWeeks: number;
  period: {
    start: string;
    end: string;
  };
}

interface WeeklyTrendChartProps {
  data: WeeklyTrendData;
  className?: string;
}

type SeriesKey = 'received' | 'processed' | 'pending' | 'discarded';

const SERIES: Array<{ key: SeriesKey; name: string; color: string; dashed?: boolean }> = [
  { key: 'received', name: 'Recebidas', color: 'var(--chart-1)' },
  { key: 'processed', name: 'Tratadas', color: 'var(--chart-3)' },
  { key: 'pending', name: 'Não Tratadas', color: 'var(--destructive)' },
  { key: 'discarded', name: 'Descartadas', color: 'var(--muted-foreground)', dashed: true }
];

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

export function WeeklyTrendChart({ data, className = '' }: WeeklyTrendChartProps) {
  if (!data || !data.weeks || data.weeks.length === 0) {
    return (
      <PainelCard title="Tendência" icon={TrendingUp} className={className}>
        <PainelEmpty icon={TrendingUp}>Dados insuficientes para gráfico de tendência</PainelEmpty>
      </PainelCard>
    );
  }

  type TooltipEntry = { color: string; name: string; value: number };
  type TooltipProps = { active?: boolean; payload?: TooltipEntry[]; label?: string };
  const TrendTooltip = ({ active, payload, label }: TooltipProps) => {
    if (!active || !payload?.length) return null;
    const week = data.weeks.find(w => w.weekLabel === label);
    return (
      <ChartTooltipBox>
        <p className="mb-2 font-semibold">Semana de {week?.weekStart && shortDate(week.weekStart)}</p>
        {payload.map(entry => (
          <p key={entry.name} className="flex items-center gap-2">
            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="font-medium">{entry.name}:</span> {entry.value}
          </p>
        ))}
      </ChartTooltipBox>
    );
  };

  const totals = data.weeks.reduce(
    (acc, week) => ({
      received: acc.received + week.received,
      processed: acc.processed + week.processed
    }),
    { received: 0, processed: 0 }
  );

  return (
    <PainelCard
      title={`Tendência (Últimas ${data.totalWeeks} Semanas)`}
      icon={TrendingUp}
      className={className}
      action={<span>{shortDate(data.period.start)} - {shortDate(data.period.end)}</span>}
    >
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data.weeks} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
          <XAxis dataKey="weekLabel" {...chartAxisProps} />
          <YAxis {...chartAxisProps} />
          <Tooltip content={<TrendTooltip />} />
          <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} iconType="line" />
          {SERIES.map(({ key, name, color, dashed }) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={name}
              stroke={color}
              strokeWidth={dashed ? 1.5 : 2}
              strokeDasharray={dashed ? '5 5' : undefined}
              dot={{ r: dashed ? 3 : 4, fill: color }}
              activeDot={{ r: dashed ? 5 : 6 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <PainelStatRow>
        <PainelStat label="Total Recebidas" value={totals.received} tone="primary" />
        <PainelStat label="Total Tratadas" value={totals.processed} />
        <PainelStat
          label="Taxa de Tratamento"
          value={`${totals.received > 0 ? Math.round((totals.processed / totals.received) * 100) : 0}%`}
        />
        <PainelStat label="Média/Semana" value={Math.round(totals.received / data.totalWeeks)} tone="muted" />
      </PainelStatRow>
    </PainelCard>
  );
}
